import { useCallback, useEffect, useRef, useState } from 'react';
import { useSocket } from 'socket.io-react-hook';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';
import { _getToken } from 'src/Config/axiosApi';
import { useTabAliveLock } from './useTabAliveLock';
import { refreshAuthToken } from 'src/Common/context/AuthContext';
import { NotificationEvent } from '../types/notifications.types';
import { compareStreamIds } from '../utils/notificationsFeed';

// Re-exported for existing importers that pull it from this hook; the
// canonical definition now lives in notificationsFeed (alongside the feed
// sort comparator that also uses it).
export { compareStreamIds };

/**
 * Seconds until a JWT's `exp`, or null if it can't be decoded. Used to
 * schedule a proactive refresh before the gateway disconnects the
 * socket for an expired token. Local copy (AuthContext's helper is not
 * exported) — only reads the standard `exp` claim.
 */
function secondsUntilTokenExpiry(token: string | null): number | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const payload = JSON.parse(
      atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')),
    ) as { exp?: number };
    if (!payload?.exp) return null;
    return payload.exp - Math.floor(Date.now() / 1000);
  } catch {
    return null;
  }
}

/**
 * Refresh the token this many seconds BEFORE it expires so a fresh
 * token is in storage before the gateway would reject the socket.
 */
const PROACTIVE_REFRESH_LEAD_SECONDS = 60;

/**
 * Cooldown between auto-refresh attempts on a connect/auth error. A
 * stale token that keeps getting rejected on every reconnect attempt
 * would otherwise hammer `/auth/refresh`. socket.io owns the reconnect
 * loop (infinite, backed off); this only throttles how often WE force a
 * token refresh in response to an auth error — the socket keeps
 * retrying regardless.
 */
const AUTH_RECOVERY_COOLDOWN_MS = 10_000;

/**
 * Hard ceiling on how long a replay window may stay open.
 *
 * The gateway emits `backfill_complete` from a `finally`, so it arrives even
 * when the `XRANGE` throws — but a pod killed mid-replay never sends it at
 * all, and a window that never closes would mark every later frame as replay
 * and mute the operator for the rest of the session. Fail OPEN (towards
 * sound): after this, treat frames as live again. Generous compared to a real
 * replay, which is one `XRANGE` round-trip.
 */
const REPLAY_WINDOW_MAX_MS = 15_000;

/** Window in which a second reconnect kick on the same socket is a duplicate. */
const KICK_DEDUPE_MS = 1_000;

/**
 * Per-SOCKET handshake state, shared by every hook instance on that socket.
 *
 * Three components call this hook over the SAME connection (the sidebar bell,
 * the app-wide sound mount, the feed page) and `socket.io-react-hook`
 * ref-counts them onto one socket. Without this each of them emitted its own
 * `subscribe_notifications`, and the gateway answers EVERY one with a full
 * `replayFrom` — three `XRANGE`s of up to `REPLAY_COUNT_CAP` (5.000) entries
 * and three `backfill_complete`s for one reconnect. `subscribe_notifications`
 * does `client.join()`, which is per socket, so one is not just enough: it is
 * the only correct number.
 *
 * `claimed` — someone already handshook this connection; cleared on
 * disconnect. `replayPending` — that handshake carried a cursor, so a replay
 * is on the wire and EVERY instance must tag its frames as replay, not only
 * the one that asked for it.
 */
interface SocketHandshake {
  claimed: boolean;
  replayPending: boolean;
  /**
   * `performance.now()` of the last reconnect kick on this socket, or `null`.
   *
   * A MONOTONIC clock, because this measures how long ago the last kick was,
   * not when it happened. A wall clock that jumps backwards — a laptop
   * resuming before NTP re-syncs, precisely when the socket is most likely to
   * be dead — would make every later kick look like a duplicate and leave
   * recovery to socket.io's backoff, throttled to about once a minute in a
   * background tab. That is the exact case the kick exists to short-circuit.
   */
  lastKickAt: number | null;
}
const handshakes = new WeakMap<object, SocketHandshake>();
function handshakeFor(socket: object): SocketHandshake {
  let state = handshakes.get(socket);
  if (!state) {
    state = { claimed: false, replayPending: false, lastKickAt: null };
    handshakes.set(socket, state);
  }
  return state;
}

/**
 * One queued frame, tagged with HOW it arrived.
 *
 * `live: false` means the frame came in during a cursor-replay window — the
 * server was catching us up after a (re)connect (`XRANGE` from our cursor,
 * up to `REPLAY_COUNT_CAP` entries) and everything it sends is, by
 * definition, something we were not connected to hear. The feed wants those
 * rows (that is the whole point of the replay: no gap after a laptop sleep,
 * a network blip or a gateway restart). The AUDIO must not: replaying a
 * hundred fills and alarms the operator already handled is the loudest way
 * to say nothing.
 *
 * The flag is deliberately NOT a field on `NotificationEvent`: that type is
 * the frontend's mirror of the backend contract in
 * `@arbitrage-core/notifications-core`, and a property that only ever exists
 * on this side of the wire would make it lie.
 */
export interface QueuedNotification {
  event: NotificationEvent;
  /** false ⇒ arrived inside a connect→`backfill_complete` replay window. */
  live: boolean;
}

/**
 * What the page consumes from this hook. A `drain()` returns and clears
 * the queue of every `notification` frame received since the last
 * drain — NO frame is overwritten (the old single-slot `latestEvent`
 * dropped same-tick bursts). `queueVersion` bumps on every enqueue so a
 * consumer effect re-runs and drains.
 */
export interface NotificationsSocketApi {
  isConnected: boolean;
  /** Bumps each time one or more frames are enqueued — drain trigger. */
  queueVersion: number;
  /** Returns + clears every queued frame, live-tagged, in FIFO order. */
  drain: () => QueuedNotification[];
  bufferedCount: number;
  resetBufferedCount: () => void;
  /** Id of the last applied stream entry (`<ms>-<seq>`), '' if none. */
  lastStreamId: string;
  /** True between a (re)connect and its `backfill_complete`. */
  isCatchingUp: boolean;
  /**
   * Bumps when the FE detects a tail gap it cannot self-heal from the
   * live stream (the next reconnect's replay closes it). The page may
   * use this as a coarse REST top-up trigger. Also bumps when the
   * server signals a possibly-incomplete replay via `backfill_complete`.
   */
  gapVersion: number;
  /**
   * Legacy single-slot surface kept ONLY for the social-notifications
   * sidebar bell (`useSocialNotifications.ts`), which is out of scope for
   * this redesign and consumes events one-at-a-time deduped by id. It is
   * a thin DERIVED view over the durable queue (the queue is the source
   * of truth). New consumers MUST use `drain`/`queueVersion`. `bufferedCount`
   * above doubles as the old badge counter. `reconnectCount` bumps on
   * every disconnect→connect transition (its only use is the social
   * bell's REST top-up on reconnect).
   */
  latestEvent: NotificationEvent | null;
  acknowledge: () => void;
  reconnectCount: number;
}

/**
 * Connects to the notifications-websocket service and exposes a durable,
 * cursor-based stream of `NotificationEvent`s.
 *
 * Reliability model (durable Redis Stream + cursor replay):
 *  - The relay forwards each `notification` frame WITH a `streamId`
 *    (the Redis Stream entry id `<ms>-<seq>`). We track the id of the
 *    most-recently-applied event in `lastStreamIdRef`.
 *  - On EVERY (re)connect we emit `subscribe_notifications` with that
 *    cursor; the gateway replays everything since it (`XRANGE`) before
 *    resuming live, then emits `backfill_complete { upTo }`. So a
 *    disconnect — gateway-pod restart, tab sleep, network blip — is
 *    recovered with no page refresh and no gap.
 *  - Frames are pushed into a QUEUE (never a single slot) so two frames
 *    delivered in the same React tick are both seen by the page.
 *
 * Auth: socket.io owns an infinite, backed-off reconnect loop. We
 * proactively refresh the JWT before `exp` and, on `connect_error` /
 * `auth_error`, refresh-and-retry without ever giving up — the socket
 * never stays permanently dead. `clearOnFailure:false` keeps a WS blip
 * from logging the operator out.
 *
 * Connection model: separate Socket.io connection from `/unwind`
 * (different host) — by design, so notification fanout can't head-of-
 * line block the market-data WS.
 */
export function useNotificationsSocket(): NotificationsSocketApi {
  const wsUrl = import.meta.env.VITE_NOTIFICATIONS_WS_URL;
  const accessToken = _getToken();

  // Keep THIS TAB out of Chromium's freezing intervention while the feed is
  // configured. A frozen tab runs no JavaScript, so it answers no engine.io
  // PING and the gateway drops it at `pingTimeout` — the operator switches to
  // another tab, comes back minutes later, and the socket has been dead the
  // whole time. It does not read as an outage either: the reconnect replays
  // the gap, so every row shows up, and only the AUDIO is missing (replayed
  // frames are muted on purpose). Keyed on the same condition as `enabled`
  // below — never on visibility. See `useTabAliveLock`.
  useTabAliveLock(!!wsUrl && !!accessToken);

  const { socket } = useSocket(wsUrl || '', {
    enabled: !!wsUrl && !!accessToken,
    transports: ['websocket'],
    // Infinite, capped-exponential reconnection — the socket must never
    // give up. A failed JWT refresh does NOT stop this loop; we just
    // keep retrying until a fresh token lets the handshake through.
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1_000,
    reconnectionDelayMax: 30_000,
    auth: (cb: (data: { token: string }) => void) =>
      cb({ token: _getToken() }),
  });

  // Ref-backed queue of received frames + a version counter so the page
  // re-runs its drain effect. We never store events in a single state
  // slot (that dropped same-tick bursts).
  const queueRef = useRef<QueuedNotification[]>([]);
  const [queueVersion, setQueueVersion] = useState(0);
  const [bufferedCount, setBufferedCount] = useState(0);

  // FE replay cursor — the id of the most-recently-applied stream entry.
  // Empty string means "never applied one" → first subscribe omits it so
  // the server seeds the client live.
  const lastStreamIdRef = useRef<string>('');
  const [lastStreamId, setLastStreamId] = useState<string>('');

  const [isCatchingUp, setIsCatchingUp] = useState(false);
  /**
   * Is a cursor replay on the wire right now? Read from the frame handler
   * (state would be a render behind) to tag each frame live-or-replay.
   *
   * Opened only when the connection's handshake actually carried a cursor: a
   * connect with no cursor replays NOTHING and the gateway never emits
   * `backfill_complete` for it (it only emits from `replayFrom`), so opening
   * one would wedge the window shut forever and mute the operator.
   */
  const replayWindowRef = useRef(false);
  /**
   * Monotonic deadline for the window above.
   *
   * The ceiling cannot be a `setTimeout` alone: a background tab throttles
   * timers to roughly once a minute, so a gateway that died mid-replay would
   * leave every frame tagged as replay — and muted — for that whole minute,
   * in exactly the backgrounded tab this feature exists to keep working. The
   * deadline is checked as each frame arrives, which no throttling can delay.
   * The timer below only drives the `isCatchingUp` UI flag.
   *
   * `performance.now()`, like every other duration on this path: a wall clock
   * that jumps backwards keeps the window open and tags live frames as
   * replay, muting every fill cue until the ceiling timer finally fires.
   */
  const replayWindowUntil = useRef(0);
  const replayWindowTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [gapVersion, setGapVersion] = useState(0);

  // Legacy single-slot surface for the out-of-scope social bell only —
  // see `NotificationsSocketApi`. Driven off the SAME frame intake below.
  const [latestEvent, setLatestEvent] = useState<NotificationEvent | null>(
    null,
  );
  const [reconnectCount, setReconnectCount] = useState(0);
  const hasDisconnected = useRef(false);

  // Last time we auto-refreshed the token in response to a connect/auth
  // error — throttles the refresh so a persistently-rejected token can't
  // loop on `/auth/refresh`. (socket.io still retries the connection.)
  // Monotonic, like every other duration here: on a wall clock a backwards
  // jump makes the elapsed comparison negative, which reads as "inside the
  // cooldown" and skips the forced token refresh for the whole skew.
  // `-Infinity` so "never recovered" cannot be confused with a stamp from a
  // second ago — `performance.now()` starts at zero.
  const lastAuthRecoveryAt = useRef(Number.NEGATIVE_INFINITY);

  // ── Frame intake: queue every `notification`, advance the cursor ──────
  //
  // We subscribe to the raw socket event (NOT `useSocketEvent`, which
  // only exposes the LATEST message) so two frames in the same tick are
  // BOTH enqueued — no overwrite. Each frame carries its `streamId`; an id
  // strictly greater than the cursor advances it, an id that is not is a
  // duplicate (typically replay overlapping live) — still enqueued, but the
  // cursor never rewinds, and the page merge dedupes by `id`. Trim-induced
  // tail gaps are surfaced separately via `backfill_complete { gap }`.
  useEffect(() => {
    const enqueue = (event: NotificationEvent) => {
      const replaying =
        replayWindowRef.current && performance.now() < replayWindowUntil.current;
      queueRef.current.push({ event, live: !replaying });
      setBufferedCount((n) => n + 1);
      setQueueVersion((n) => n + 1);
      setLatestEvent(event); // legacy single-slot view (social bell only)
    };
    const onNotification = (event: NotificationEvent) => {
      const incomingId = event.streamId;
      if (incomingId) {
        const cursor = lastStreamIdRef.current;
        const cmp = cursor ? compareStreamIds(incomingId, cursor) : 1;
        if (cmp <= 0) {
          // Not strictly greater than the cursor → duplicate (typically
          // replay overlapping live). The page merge dedupes by `id`, so
          // we still enqueue but do NOT rewind the cursor.
          enqueue(event);
          return;
        }
        lastStreamIdRef.current = incomingId;
        setLastStreamId(incomingId);
      }
      enqueue(event);
    };

    socket.on('notification', onNotification);
    return () => {
      socket.off('notification', onNotification);
    };
  }, [socket]);

  // ── Replay handshake: subscribe with the cursor on every (re)connect ──
  useEffect(() => {
    const handshake = handshakeFor(socket);
    /**
     * Close THIS instance's window. Says nothing about the socket.
     */
    const closeLocalReplayWindow = () => {
      replayWindowRef.current = false;
      replayWindowUntil.current = 0;
      setIsCatchingUp(false);
      if (replayWindowTimer.current) {
        clearTimeout(replayWindowTimer.current);
        replayWindowTimer.current = null;
      }
    };

    /**
     * Close the window for the whole SOCKET: this instance's, plus the shared
     * flag that decides whether an instance mounting later opens one of its
     * own.
     *
     * Only two things may say this — `backfill_complete` (the replay is over)
     * and the ceiling (we assume it is). ⚠️ NOT the effect cleanup: one
     * component unmounting is not a statement about the socket, and clearing
     * the shared flag from there let an instance mounting afterwards read
     * "no replay pending" while a replay was still on the wire, and tag its
     * frames as live.
     */
    const closeReplayWindowForSocket = () => {
      handshake.replayPending = false;
      closeLocalReplayWindow();
    };

    const subscribe = () => {
      // One handshake per connection, whichever instance gets here first.
      if (!handshake.claimed) {
        const cursor = lastStreamIdRef.current;
        handshake.claimed = true;
        handshake.replayPending = !!cursor;
        socket.emit(
          'subscribe_notifications',
          cursor ? { lastStreamId: cursor } : {},
        );
      }
      // Opened by EVERY instance when a replay is on the wire — the frames go
      // to the socket, so they reach the instances that did not ask for them.
      if (handshake.replayPending) {
        replayWindowRef.current = true;
        replayWindowUntil.current = performance.now() + REPLAY_WINDOW_MAX_MS;
        setIsCatchingUp(true);
        if (replayWindowTimer.current) clearTimeout(replayWindowTimer.current);
        replayWindowTimer.current = setTimeout(
          closeReplayWindowForSocket,
          REPLAY_WINDOW_MAX_MS,
        );
      }
      if (hasDisconnected.current) {
        // Genuine reconnect (not the first connect after mount) — bump
        // for the out-of-scope social bell's REST top-up.
        hasDisconnected.current = false;
        setReconnectCount((n) => n + 1);
      }
    };
    const onDisconnect = () => {
      hasDisconnected.current = true;
      // Next connection needs its own handshake. `replayPending` is
      // deliberately NOT touched: nothing reads it while disconnected, and the
      // next `subscribe()` reassigns it from its own cursor — clearing it here
      // would be an assignment nothing can ever observe.
      handshake.claimed = false;
    };

    const onBackfillComplete = (data?: { upTo?: string; gap?: boolean }) => {
      closeReplayWindowForSocket();
      const upTo = data?.upTo;
      // The server sets `gap: true` when our cursor was trimmed past the
      // stream floor — the authoritative signal, since the replayed frames
      // advance our cursor PAST `lastStreamId` and a FE-only `upTo < cursor`
      // check can never observe the trim. We also keep the `upTo < cursor`
      // check as a defensive secondary (older gateways without the flag).
      const cursorTrimmed = data?.gap === true;
      const upToBelowCursor =
        !!upTo &&
        !!lastStreamIdRef.current &&
        compareStreamIds(upTo, lastStreamIdRef.current) < 0;
      if (cursorTrimmed || upToBelowCursor) {
        Sentry.captureMessage(
          'notifications WS replay incomplete — cursor older than stream floor',
          { level: 'warning', tags: { area: 'notifications-ws' } },
        );
        setGapVersion((n) => n + 1);
      }
    };

    /**
     * A backgrounded tab must stay LIVE — the operator can be on another
     * monitor, another app or another browser tab and still needs the feed.
     * Nothing in this hook ever disconnects on hide, and these two listeners
     * are the recovery side of that promise: when the BROWSER killed the
     * socket anyway (a discarded tab, a laptop that slept, a network change),
     * socket.io's own retry runs on timers, and timers in a background tab
     * are throttled to about once a minute. Coming back to the tab, or the
     * network coming back, kicks a connect immediately instead.
     *
     * 🚨 CONNECT-ONLY. Neither of these may ever call `disconnect()`, and
     * `visibilitychange` must never grow a `hidden` branch here. A tab going
     * to the background is not a reason to drop anything — that symmetry is
     * exactly the bug that made the 2026-05-23 sound-leader go silent, and it
     * would be far worse on the transport.
     */
    /**
     * Dial out NOW, bypassing socket.io's pending backoff.
     *
     * 🚨 `socket.connect()` ALONE IS A NO-OP HERE, which is the whole reason
     * this helper exists. The client reads:
     *
     *     connect() { ... if (!this.io["_reconnecting"]) this.io.open(); ... }
     *
     * and `Manager.reconnect()` sets `_reconnecting = true` BEFORE scheduling
     * the backoff timer, keeping it true for the entire delay. With
     * `reconnectionAttempts: Infinity` there is no exit from that state
     * between attempts — so during exactly the window we are trying to
     * short-circuit, `connect()` skips the dial and returns. Verified against
     * `socket.io-client` 4.7 in this repo.
     *
     * 🚨 AND `io.open()` IS NOT THE WAY AROUND IT. `_reconnecting` is only
     * ever cleared from the callback `reconnect()` hands to `open(fn)`. Open
     * the manager ourselves, without that callback, and the pending backoff
     * timer later finds `_readyState` already "opening", returns early, and
     * NEVER invokes `fn` — so `_reconnecting` stays true for the rest of the
     * session. The next `onclose` then hits
     * `reconnect() { if (this._reconnecting) return; }` and schedules nothing:
     * the socket is dead, silently, with only the `disconnected` chip to show
     * for it. Measured on `socket.io-client` 4.7.5 — the FIRST kick recovers
     * and the SECOND disconnect never comes back.
     *
     * So we take the manager all the way down and back up, which is what the
     * auth-error recovery below already does. `disconnect()` reaches
     * `Manager._close()`: `skipReconnect = true`, `_reconnecting = false`, and
     * `onclose()` → `cleanup()` clears the pending backoff timer and resets
     * the attempt counter. `connect()` then takes the `!_reconnecting` branch,
     * dials, and `open()` puts `skipReconnect` back to false — so if THIS
     * attempt fails, the ordinary backoff loop arms itself again.
     */
    const kick = () => {
      if (socket.connected) return;
      // One kick per socket, not one per hook instance. Three components mount
      // this hook over the same connection, so a single alt-tab fired three
      // full close/open cycles at the manager — two of them aborting the dial
      // the previous one had just started, and each resetting socket.io's
      // backoff. It recovers, but it is 3× the churn on every alt-tab and
      // every `online` of a flaky network.
      const now = performance.now();
      if (
        handshake.lastKickAt !== null &&
        now - handshake.lastKickAt < KICK_DEDUPE_MS
      ) {
        return;
      }
      handshake.lastKickAt = now;
      // 🚨 There may be no transport to kick. When `enabled` is false — no WS
      // url, or no token yet — `socket.io-react-hook` hands back a
      // `SocketMock` instead of a `Socket`: `connected` is `undefined` (so the
      // guard above waves it through) and there is NO `connect` method at all.
      // These three listeners fire on every alt-tab and every network change,
      // with or without a socket, so without this the mock path throws an
      // uncaught TypeError inside a DOM listener — once per hook instance, for
      // the whole session. Staging has no `VITE_NOTIFICATIONS_WS_URL` at all,
      // so that is every alt-tab of every QA session, straight into Sentry.
      if (typeof socket.connect !== 'function') return;
      socket.disconnect();
      socket.connect();
    };
    const onVisibilityChange = () => {
      // Going hidden is never a reason to dial out; coming back is.
      if (document.visibilityState === 'hidden') return;
      kick();
    };
    // 🚨 Whatever else changes here, none of these listeners may ever call
    // `disconnect()`. A tab going to the background is not a reason to drop a
    // socket carrying naked-leg alarms.
    const onOnline = () => kick();
    /**
     * A page Chrome froze and later unfroze WHILE STILL HIDDEN gets no
     * `visibilitychange` at all, and a frozen page answered no engine.io PING,
     * so the gateway has already dropped its socket. Without this the tab
     * waits out socket.io's retry on throttled background timers. Deliberately
     * NOT gated on visibility: the whole point is that this tab is still in
     * the background.
     */
    const onResume = () => kick();

    socket.on('connect', subscribe);
    socket.on('disconnect', onDisconnect);
    socket.on('backfill_complete', onBackfillComplete);
    document.addEventListener('visibilitychange', onVisibilityChange);
    document.addEventListener('resume', onResume);
    window.addEventListener('online', onOnline);
    // Mounted onto an ALREADY-connected socket: the `connect` listener above
    // will not fire for a connection that already happened, so subscribe now.
    // `socket.connected` is deliberately NOT a dependency of this effect:
    // making it one re-runs the whole body on every connect/disconnect
    // re-render, closing and reopening the replay window for no reason.
    // ⚠️ It would NOT double the handshake any more — `handshake.claimed`
    // blocks that now — so do not add the dep believing the claim covers the
    // rest. It does not; it covers exactly the emit.
    if (socket.connected) subscribe();
    else hasDisconnected.current = true;

    return () => {
      socket.off('connect', subscribe);
      socket.off('disconnect', onDisconnect);
      socket.off('backfill_complete', onBackfillComplete);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      document.removeEventListener('resume', onResume);
      window.removeEventListener('online', onOnline);
      // Reset OUR window, not the socket's: leaving it open with the ceiling
      // cleared would tag every later frame as replay and mute the operator
      // until the next connect re-armed it. The shared flag is not ours to
      // clear — another instance may still be mid-replay.
      closeLocalReplayWindow();
    };
  }, [socket]);

  // ── Proactive token refresh before `exp` ──────────────────────────────
  //
  // The gateway disconnects a socket whose token expired. Refreshing
  // ahead of `exp` means the `auth` callback always re-reads a still-
  // valid token on the next (re)connect, so we rarely hit an auth error
  // at all. Re-schedules itself whenever the token changes.
  useEffect(() => {
    const ttl = secondsUntilTokenExpiry(accessToken);
    if (ttl === null) return;
    const delayMs = Math.max(0, (ttl - PROACTIVE_REFRESH_LEAD_SECONDS) * 1000);
    const timer = setTimeout(() => {
      refreshAuthToken({ clearOnFailure: false }).catch((err) => {
        Sentry.captureException(err, { tags: { area: 'notifications-ws' } });
      });
    }, delayMs);
    return () => clearTimeout(timer);
  }, [accessToken]);

  // ── Auth-error recovery: refresh-and-retry, NEVER give up ─────────────
  //
  // socket.io keeps reconnecting on its own (infinite + backoff). On an
  // auth error we additionally refresh the token (once per cooldown) so
  // the next automatic reconnect carries a fresh one. We never stop the
  // loop and never log the operator out (`clearOnFailure:false`).
  useEffect(() => {
    const handleAuthError = async (err?: unknown) => {
      const now = performance.now();
      if (now - lastAuthRecoveryAt.current < AUTH_RECOVERY_COOLDOWN_MS) {
        Sentry.captureException(
          err instanceof Error ? err : new Error('notifications WS auth error'),
          { tags: { area: 'notifications-ws' } },
        );
        return;
      }
      lastAuthRecoveryAt.current = now;

      let newToken: string | null = null;
      try {
        // `clearOnFailure: false` — a notifications-WS problem must NEVER
        // wipe the primary session. On refresh failure we stay silent
        // and let socket.io keep retrying; the next API 401 owns the
        // destructive logout decision.
        newToken = await refreshAuthToken({ clearOnFailure: false });
      } catch (refreshErr) {
        Sentry.captureException(refreshErr, {
          tags: { area: 'notifications-ws' },
        });
      }

      if (newToken) {
        // Nudge an immediate reconnect with the fresh token instead of
        // waiting out the backoff. `connect()` is a no-op if already
        // connecting.
        try {
          socket.disconnect();
        } catch {
          /* already closed */
        }
        socket.connect();
      } else {
        // Refresh failed — do NOT give up. socket.io's own infinite
        // reconnect loop keeps trying; a later proactive/manual refresh
        // (or the operator re-auth) will let the handshake through. We
        // surface a single toast so the operator knows the feed is
        // catching up, never a permanently-dead socket.
        toast.error(
          'Notifications reconnecting — will recover automatically.',
          { toastId: 'notifications-ws-auth' },
        );
        Sentry.captureException(
          err instanceof Error
            ? err
            : new Error('notifications WS auth error — refresh failed'),
          { tags: { area: 'notifications-ws' } },
        );
      }
    };

    socket.on('connect_error', handleAuthError);
    socket.on('auth_error', handleAuthError);
    return () => {
      socket.off('connect_error', handleAuthError);
      socket.off('auth_error', handleAuthError);
    };
  }, [socket]);

  /** Returns + clears every queued frame, live-tagged, in FIFO order. */
  const drain = useCallback((): QueuedNotification[] => {
    if (queueRef.current.length === 0) return [];
    const batch = queueRef.current;
    queueRef.current = [];
    return batch;
  }, []);

  /** Reset the buffered count badge (e.g. after the page refocuses). */
  const resetBufferedCount = useCallback(() => {
    setBufferedCount(0);
  }, []);

  /** Legacy: clear the single-slot view after the social bell consumed it. */
  const acknowledge = useCallback(() => {
    setLatestEvent(null);
  }, []);

  return {
    isConnected: socket.connected,
    queueVersion,
    drain,
    bufferedCount,
    resetBufferedCount,
    lastStreamId,
    isCatchingUp,
    gapVersion,
    latestEvent,
    acknowledge,
    reconnectCount,
  };
}
